import { useEffect, useState } from 'react';

export const PROJECT_VIEWS = ['spark', 'research', 'moodboard', 'map', 'history', 'members'] as const;
export type ProjectView = (typeof PROJECT_VIEWS)[number];

/** `focus`: a place card to centre the Map on (#/p/<id>/map?place=<cardId>). */
export type Route = { name: 'projects' } | { name: 'project'; id: string; view: ProjectView; focus: string | null } | { name: 'share' } | { name: 'settings' };

export function parseRoute(hash: string): Route {
  const [rawPath, query = ''] = hash.replace(/^#/, '').split('?');
  const path = rawPath || '/';
  const m = path.match(/^\/p\/([\w-]+)(?:\/(\w+))?$/);
  if (m) {
    const view = (PROJECT_VIEWS as readonly string[]).includes(m[2] ?? '') ? (m[2] as ProjectView) : 'research';
    return { name: 'project', id: m[1], view, focus: new URLSearchParams(query).get('place') };
  }
  if (path === '/share') return { name: 'share' };
  if (path === '/settings') return { name: 'settings' };
  return { name: 'projects' };
}

export const href = {
  projects: () => '#/',
  project: (id: string, view: ProjectView = 'research') => `#/p/${id}/${view}`,
  /** The Map, centred on one place. */
  place: (id: string, placeId: string) => `#/p/${id}/map?place=${encodeURIComponent(placeId)}`,
  settings: () => '#/settings',
};

export function navigate(to: string) {
  window.location.hash = to.replace(/^#/, '');
}

export function useRoute(): Route {
  const [route, setRoute] = useState(() => parseRoute(window.location.hash));
  useEffect(() => {
    // Reads the hash this specific event carried (`newURL`), not a fresh `window.location.hash`:
    // if two hash writes happen close enough together that their `hashchange` dispatches both
    // land before either handler runs, a live re-read would see the *second* write's value for
    // *both* events, silently dropping the first transition (e.g. tapping a tab then immediately
    // pressing the browser's Back button could otherwise never deliver the tab's own route change).
    const on = (e: HashChangeEvent) => setRoute(parseRoute(new URL(e.newURL).hash));
    window.addEventListener('hashchange', on);
    return () => window.removeEventListener('hashchange', on);
  }, []);
  return route;
}

/** Phone layout: narrow screens, or touch-only devices (spec §8). */
export function usePhone(): boolean {
  const query = '(max-width: 767px), (pointer: coarse) and (hover: none)';
  const [phone, setPhone] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const mq = window.matchMedia(query);
    const on = () => setPhone(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  return phone;
}
